import { useState, useRef } from 'react';
import {
  ArrowLeft,
  Bot,
  Brain,
  Cpu,
  Eye,
  Layers,
  Palette,
  Radio,
  Shield,
  Sparkles,
  Terminal,
  Zap,
  ChevronRight,
  Sliders,
  BookOpen,
  Network,
  Activity,
  Music,
  Camera,
  Upload,
} from 'lucide-react';
import {
  getStemAutoSeparate,
  getStemModel,
  getStemCount,
  setStemAutoSeparate,
  setStemModel,
  setStemCount,
  getModelInfo,
  STEM_MODELS,
  StemModelPref,
} from '../../lib/supabase';

interface AgentSettingsProps {
  onBack: () => void;
}

type TabKey = 'identity' | 'skills' | 'behavior' | 'audio' | 'visuals' | 'capabilities';

interface Skill {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  category: 'core' | 'creative' | 'analytical' | 'integration';
}

const DEFAULT_SKILLS: Skill[] = [
  { id: 'batch_approve', name: 'Batch Approve', description: 'Approve multiple tracks in a single operation', enabled: true, category: 'core' },
  { id: 'batch_publish', name: 'Batch Publish', description: 'Publish approved tracks to distribution', enabled: true, category: 'core' },
  { id: 'batch_review', name: 'Batch Review', description: 'Queue tracks for review with priority scoring', enabled: true, category: 'core' },
  { id: 'batch_retag', name: 'Batch Retag', description: 'Re-tag metadata across multiple assets', enabled: true, category: 'core' },
  { id: 'bulk_solve', name: 'Alignment Solve', description: 'Resolve timing alignment conflicts automatically', enabled: true, category: 'analytical' },
  { id: 'export', name: 'Catalog Export', description: 'Export catalog data in multiple formats', enabled: true, category: 'integration' },
  { id: 'batch_delete', name: 'Batch Delete', description: 'Permanently remove tracks (expert only)', enabled: false, category: 'core' },
  { id: 'auto_tag', name: 'Auto-Tagging', description: 'ML-powered automatic metadata tagging', enabled: true, category: 'creative' },
  { id: 'sentiment', name: 'Sentiment Analysis', description: 'Analyze track mood and emotional profile', enabled: false, category: 'analytical' },
  { id: 'similarity', name: 'Similarity Detection', description: 'Find duplicate or similar tracks', enabled: true, category: 'analytical' },
  { id: 'webhook_relay', name: 'Webhook Relay', description: 'Forward events to external services', enabled: true, category: 'integration' },
  { id: 'api_bridge', name: 'API Bridge', description: 'Connect to third-party music APIs', enabled: false, category: 'integration' },
  { id: 'stem_separate', name: 'Stem Separation', description: 'Demucs AI source separation (vocals, drums, bass, guitar, piano)', enabled: true, category: 'creative' },
];

const CATEGORY_LABELS: Record<string, string> = {
  core: 'Core Operations',
  creative: 'Creative Intelligence',
  analytical: 'Analytical',
  integration: 'Integrations',
};

const CATEGORY_COLORS: Record<string, string> = {
  core: '#00FFFF',
  creative: '#FF6BFF',
  analytical: '#FFD600',
  integration: '#39FF14',
};

export function AgentSettings({ onBack }: AgentSettingsProps) {
  const [activeTab, setActiveTab] = useState<TabKey>('identity');
  const [agentName, setAgentName] = useState('FredBOT');
  const [agentDescription, setAgentDescription] = useState('Autonomous music catalog management agent');
  const [agentAvatar, setAgentAvatar] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    return window.localStorage.getItem('pipes.agent_avatar') ?? '';
  });
  const [personality, setPersonality] = useState<'efficient' | 'conversational' | 'minimal'>('efficient');
  const [autonomy, setAutonomy] = useState(70);
  const [confirmThreshold, setConfirmThreshold] = useState<'always' | 'destructive' | 'never'>('destructive');
  const [maxConcurrent, setMaxConcurrent] = useState(5);
  const [defaultLayer, setDefaultLayer] = useState<'basic' | 'advanced' | 'expert'>('advanced');
  const [accentColor, setAccentColor] = useState('#00FFFF');
  const [glowIntensity, setGlowIntensity] = useState(60);
  const [animationSpeed, setAnimationSpeed] = useState<'slow' | 'normal' | 'fast'>('normal');
  const [skills, setSkills] = useState<Skill[]>(DEFAULT_SKILLS);
  const [reasoningVisible, setReasoningVisible] = useState(true);
  const [streamThoughts, setStreamThoughts] = useState(true);
  const [swarmEnabled, setSwarmEnabled] = useState(true);
  const [stemAutoSeparate, setStemAutoState] = useState(() => getStemAutoSeparate());
  const [stemModel, setStemModelState] = useState<StemModelPref>(() => getStemModel());
  const [stemCountVal, setStemCountVal] = useState(() => getStemCount());

  function toggleSkill(id: string) {
    setSkills(prev => prev.map(s => s.id === id ? { ...s, enabled: !s.enabled } : s));
  }

  const tabs: { key: TabKey; label: string; icon: typeof Bot }[] = [
    { key: 'identity', label: 'Identity', icon: Bot },
    { key: 'skills', label: 'Skills', icon: Zap },
    { key: 'behavior', label: 'Behavior', icon: Brain },
    { key: 'audio', label: 'Audio Processing', icon: Activity },
    { key: 'visuals', label: 'Visuals', icon: Palette },
    { key: 'capabilities', label: 'Capabilities', icon: Shield },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-[#030609] flex flex-col">
      {/* Header */}
      <div className="shrink-0 border-b border-white/[0.08] bg-black/40 backdrop-blur-[30px]">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-white/[0.08] text-white/60 hover:text-white hover:border-[#00FFFF]/40 hover:bg-[#00FFFF]/[0.04] transition-all duration-200"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-base font-semibold text-white tracking-tight">Configure Agent</h1>
              <p className="text-[11px] text-white/40 font-mono mt-0.5">mediablox-agent/1.0 &middot; {agentName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#00FFFF] animate-pulse shadow-[0_0_6px_#00FFFF]" />
            <span className="text-[10px] font-mono text-[#00FFFF]/80 uppercase tracking-wider">Online</span>
          </div>
        </div>

        {/* Tab navigation */}
        <div className="px-6 flex gap-0 overflow-x-auto scrollbar-thin">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-[11px] font-medium tracking-wide border-b-2 transition-all duration-200 whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-[#00FFFF] text-[#00FFFF]'
                  : 'border-transparent text-white/40 hover:text-white/70'
              }`}
            >
              <tab.icon size={13} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-8">
          {activeTab === 'identity' && (
            <IdentityTab
              agentName={agentName}
              setAgentName={setAgentName}
              agentDescription={agentDescription}
              setAgentDescription={setAgentDescription}
              personality={personality}
              setPersonality={setPersonality}
              agentAvatar={agentAvatar}
              setAgentAvatar={(url) => { setAgentAvatar(url); window.localStorage.setItem('pipes.agent_avatar', url); }}
            />
          )}
          {activeTab === 'skills' && (
            <SkillsTab skills={skills} toggleSkill={toggleSkill} />
          )}
          {activeTab === 'behavior' && (
            <BehaviorTab
              autonomy={autonomy}
              setAutonomy={setAutonomy}
              confirmThreshold={confirmThreshold}
              setConfirmThreshold={setConfirmThreshold}
              maxConcurrent={maxConcurrent}
              setMaxConcurrent={setMaxConcurrent}
              defaultLayer={defaultLayer}
              setDefaultLayer={setDefaultLayer}
              reasoningVisible={reasoningVisible}
              setReasoningVisible={setReasoningVisible}
              streamThoughts={streamThoughts}
              setStreamThoughts={setStreamThoughts}
              swarmEnabled={swarmEnabled}
              setSwarmEnabled={setSwarmEnabled}
            />
          )}
          {activeTab === 'audio' && (
            <AudioProcessingTab
              autoSeparate={stemAutoSeparate}
              setAutoSeparate={(v) => { setStemAutoState(v); setStemAutoSeparate(v); }}
              model={stemModel}
              setModel={(v) => { setStemModelState(v); setStemModel(v); const info = getModelInfo(v); if (stemCountVal > info.maxStems) { setStemCountVal(info.maxStems); setStemCount(info.maxStems); } }}
              stemCount={stemCountVal}
              setStemCount={(v) => { setStemCountVal(v); setStemCount(v); }}
            />
          )}
          {activeTab === 'visuals' && (
            <VisualsTab
              accentColor={accentColor}
              setAccentColor={setAccentColor}
              glowIntensity={glowIntensity}
              setGlowIntensity={setGlowIntensity}
              animationSpeed={animationSpeed}
              setAnimationSpeed={setAnimationSpeed}
            />
          )}
          {activeTab === 'capabilities' && <CapabilitiesTab />}
        </div>
      </div>
    </div>
  );
}

function IdentityTab({
  agentName, setAgentName, agentDescription, setAgentDescription, personality, setPersonality, agentAvatar, setAgentAvatar,
}: {
  agentName: string; setAgentName: (v: string) => void;
  agentDescription: string; setAgentDescription: (v: string) => void;
  personality: string; setPersonality: (v: 'efficient' | 'conversational' | 'minimal') => void;
  agentAvatar: string; setAgentAvatar: (v: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setAgentAvatar(result);
    };
    reader.readAsDataURL(file);
  }

  return (
    <div className="space-y-8">
      <SectionHeader icon={Bot} title="Agent Identity" subtitle="Core identity and personality configuration" />

      {/* Agent Avatar */}
      <div className="flex items-center gap-6">
        <div className="relative group">
          <div className="w-20 h-20 rounded-xl border-2 border-[#00FFFF]/30 bg-[#00FFFF]/[0.04] overflow-hidden flex items-center justify-center shadow-[0_0_20px_rgba(0,255,255,0.08)]">
            {agentAvatar ? (
              <img src={agentAvatar} alt="Agent" className="w-full h-full object-cover" />
            ) : (
              <Bot size={32} className="text-[#00FFFF]/40" />
            )}
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="absolute inset-0 rounded-xl bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center"
          >
            <Camera size={18} className="text-white" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
        <div className="flex-1">
          <div className="text-[12px] font-medium text-white/70 mb-1">Agent Avatar</div>
          <div className="text-[10px] text-white/30 mb-3">Upload an image to represent this agent across the interface</div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="text-[10px] font-bold uppercase tracking-[0.1em] px-3 py-1.5 rounded border border-white/20 text-white/60 hover:border-[#00FFFF]/40 hover:text-[#00FFFF] transition-all flex items-center gap-1.5"
            >
              <Upload size={10} /> Upload Image
            </button>
            {agentAvatar && (
              <button
                onClick={() => setAgentAvatar('')}
                className="text-[10px] font-bold uppercase tracking-[0.1em] px-3 py-1.5 rounded border border-white/10 text-gray-500 hover:text-red-400 hover:border-red-400/30 transition-all"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FieldGroup label="Agent Name" hint="Display name across the interface">
          <input
            type="text"
            value={agentName}
            onChange={e => setAgentName(e.target.value)}
            className="w-full bg-white/[0.04] border border-white/[0.1] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#00FFFF]/50 focus:ring-1 focus:ring-[#00FFFF]/20 transition-all"
          />
        </FieldGroup>

        <FieldGroup label="Protocol" hint="Agent communication protocol">
          <div className="w-full bg-white/[0.02] border border-white/[0.06] rounded-lg px-3 py-2.5 text-sm text-white/50 font-mono">
            mediablox-agent/1.0
          </div>
        </FieldGroup>
      </div>

      <FieldGroup label="Description" hint="Brief description of the agent's purpose">
        <textarea
          value={agentDescription}
          onChange={e => setAgentDescription(e.target.value)}
          rows={2}
          className="w-full bg-white/[0.04] border border-white/[0.1] rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#00FFFF]/50 focus:ring-1 focus:ring-[#00FFFF]/20 transition-all resize-none"
        />
      </FieldGroup>

      <FieldGroup label="Personality Mode" hint="Determines the agent's communication style">
        <div className="grid grid-cols-3 gap-3">
          {(['efficient', 'conversational', 'minimal'] as const).map(p => (
            <button
              key={p}
              onClick={() => setPersonality(p)}
              className={`px-3 py-3 rounded-lg border text-[11px] font-medium capitalize transition-all duration-200 ${
                personality === p
                  ? 'border-[#00FFFF]/50 bg-[#00FFFF]/[0.06] text-[#00FFFF] shadow-[0_0_12px_rgba(0,255,255,0.08)]'
                  : 'border-white/[0.08] bg-white/[0.02] text-white/50 hover:border-white/[0.15] hover:text-white/70'
              }`}
            >
              <div className="text-center">
                <div className="mb-1">
                  {p === 'efficient' && <Cpu size={16} className="mx-auto" />}
                  {p === 'conversational' && <BookOpen size={16} className="mx-auto" />}
                  {p === 'minimal' && <Terminal size={16} className="mx-auto" />}
                </div>
                {p}
              </div>
            </button>
          ))}
        </div>
      </FieldGroup>

      <div className="p-4 rounded-lg border border-[#00FFFF]/10 bg-[#00FFFF]/[0.02]">
        <div className="flex items-start gap-3">
          <Sparkles size={14} className="text-[#00FFFF] mt-0.5 shrink-0" />
          <div>
            <div className="text-[11px] text-white/70 leading-relaxed">
              The agent adapts its responses based on personality mode.
              <span className="text-[#00FFFF]"> Efficient</span> minimizes verbosity and focuses on results.
              <span className="text-[#00FFFF]"> Conversational</span> provides explanations and context.
              <span className="text-[#00FFFF]"> Minimal</span> uses machine-readable output only.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SkillsTab({ skills, toggleSkill }: { skills: Skill[]; toggleSkill: (id: string) => void }) {
  const categories = ['core', 'creative', 'analytical', 'integration'] as const;
  const enabledCount = skills.filter(s => s.enabled).length;

  return (
    <div className="space-y-8">
      <SectionHeader
        icon={Zap}
        title="Skills & Actions"
        subtitle={`${enabledCount} of ${skills.length} skills active`}
      />

      {categories.map(cat => {
        const catSkills = skills.filter(s => s.category === cat);
        if (catSkills.length === 0) return null;
        return (
          <div key={cat}>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[cat] }} />
              <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-white/50">{CATEGORY_LABELS[cat]}</span>
              <span className="text-[9px] font-mono text-white/30">{catSkills.filter(s => s.enabled).length}/{catSkills.length}</span>
            </div>
            <div className="space-y-1.5">
              {catSkills.map(skill => (
                <button
                  key={skill.id}
                  onClick={() => toggleSkill(skill.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border transition-all duration-200 group ${
                    skill.enabled
                      ? 'border-white/[0.08] bg-white/[0.03] hover:border-white/[0.15]'
                      : 'border-white/[0.04] bg-white/[0.01] hover:border-white/[0.08] opacity-60'
                  }`}
                >
                  <div
                    className={`relative inline-flex h-5 w-10 items-center rounded-full shrink-0 transition-colors duration-300 ${
                      skill.enabled ? 'bg-[#39FF14]' : 'bg-white/15'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full shadow-md transition-transform duration-300 ${
                        skill.enabled ? 'translate-x-5 bg-black' : 'translate-x-0.5 bg-white'
                      }`}
                    />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className={`text-[12px] font-mono ${skill.enabled ? 'text-white' : 'text-white/40'}`}>{skill.name}</div>
                    <div className="text-[10px] text-white/30 truncate">{skill.description}</div>
                  </div>
                  <ChevronRight size={12} className="text-white/20 group-hover:text-white/40 transition-colors" />
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BehaviorTab({
  autonomy, setAutonomy, confirmThreshold, setConfirmThreshold,
  maxConcurrent, setMaxConcurrent, defaultLayer, setDefaultLayer,
  reasoningVisible, setReasoningVisible, streamThoughts, setStreamThoughts,
  swarmEnabled, setSwarmEnabled,
}: {
  autonomy: number; setAutonomy: (v: number) => void;
  confirmThreshold: string; setConfirmThreshold: (v: 'always' | 'destructive' | 'never') => void;
  maxConcurrent: number; setMaxConcurrent: (v: number) => void;
  defaultLayer: string; setDefaultLayer: (v: 'basic' | 'advanced' | 'expert') => void;
  reasoningVisible: boolean; setReasoningVisible: (v: boolean) => void;
  streamThoughts: boolean; setStreamThoughts: (v: boolean) => void;
  swarmEnabled: boolean; setSwarmEnabled: (v: boolean) => void;
}) {
  return (
    <div className="space-y-8">
      <SectionHeader icon={Brain} title="Behavior & Autonomy" subtitle="Control how the agent makes decisions" />

      <FieldGroup label="Autonomy Level" hint={`${autonomy}% — ${autonomy > 75 ? 'Agent decides most actions independently' : autonomy > 40 ? 'Agent confirms on important decisions' : 'Agent confirms on all decisions'}`}>
        <div className="relative">
          <input
            type="range"
            min={0}
            max={100}
            value={autonomy}
            onChange={e => setAutonomy(Number(e.target.value))}
            className="w-full h-1.5 bg-white/[0.08] rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#00FFFF] [&::-webkit-slider-thumb]:shadow-[0_0_8px_#00FFFF]"
          />
          <div className="flex justify-between mt-2 text-[9px] font-mono text-white/30">
            <span>Manual</span>
            <span>Supervised</span>
            <span>Autonomous</span>
          </div>
        </div>
      </FieldGroup>

      <FieldGroup label="Confirmation Requirement" hint="When should the agent ask for approval?">
        <div className="grid grid-cols-3 gap-3">
          {(['always', 'destructive', 'never'] as const).map(t => (
            <button
              key={t}
              onClick={() => setConfirmThreshold(t)}
              className={`px-3 py-2.5 rounded-lg border text-[11px] font-medium capitalize transition-all duration-200 ${
                confirmThreshold === t
                  ? 'border-[#00FFFF]/50 bg-[#00FFFF]/[0.06] text-[#00FFFF]'
                  : 'border-white/[0.08] bg-white/[0.02] text-white/50 hover:border-white/[0.15]'
              }`}
            >
              {t === 'destructive' ? 'Destructive Only' : t}
            </button>
          ))}
        </div>
      </FieldGroup>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FieldGroup label="Max Concurrent Tasks" hint="Parallel execution limit">
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={1}
              max={20}
              value={maxConcurrent}
              onChange={e => setMaxConcurrent(Number(e.target.value))}
              className="flex-1 h-1.5 bg-white/[0.08] rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#00FFFF] [&::-webkit-slider-thumb]:shadow-[0_0_8px_#00FFFF]"
            />
            <span className="text-sm font-mono text-[#00FFFF] w-6 text-right tabular-nums">{maxConcurrent}</span>
          </div>
        </FieldGroup>

        <FieldGroup label="Default Layer" hint="Starting capability surface">
          <div className="grid grid-cols-3 gap-2">
            {(['basic', 'advanced', 'expert'] as const).map(l => (
              <button
                key={l}
                onClick={() => setDefaultLayer(l)}
                className={`px-2 py-2 rounded-lg border text-[11px] font-medium capitalize transition-all duration-200 ${
                  defaultLayer === l
                    ? 'border-[#00FFFF]/50 bg-[#00FFFF]/[0.06] text-[#00FFFF]'
                    : 'border-white/[0.08] bg-white/[0.02] text-white/50 hover:border-white/[0.15]'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </FieldGroup>
      </div>

      <div className="space-y-3">
        <ToggleRow
          label="Show Reasoning"
          description="Display chain-of-thought in the execution stream"
          enabled={reasoningVisible}
          onToggle={() => setReasoningVisible(!reasoningVisible)}
        />
        <ToggleRow
          label="Stream Thoughts"
          description="Show intermediate reasoning steps in real-time"
          enabled={streamThoughts}
          onToggle={() => setStreamThoughts(!streamThoughts)}
        />
        <ToggleRow
          label="Swarm Coordination"
          description="Enable multi-node parallel processing"
          enabled={swarmEnabled}
          onToggle={() => setSwarmEnabled(!swarmEnabled)}
        />
      </div>
    </div>
  );
}

function VisualsTab({
  accentColor, setAccentColor, glowIntensity, setGlowIntensity,
  animationSpeed, setAnimationSpeed,
}: {
  accentColor: string; setAccentColor: (v: string) => void;
  glowIntensity: number; setGlowIntensity: (v: number) => void;
  animationSpeed: string; setAnimationSpeed: (v: 'slow' | 'normal' | 'fast') => void;
}) {
  const presetColors = ['#00FFFF', '#39FF14', '#FF003C', '#FFD600', '#FF6BFF', '#4D7CFF'];

  return (
    <div className="space-y-8">
      <SectionHeader icon={Palette} title="Visual Preferences" subtitle="Customize the agent's visual appearance" />

      <FieldGroup label="Accent Color" hint="Primary neon color used across the agent interface">
        <div className="flex items-center gap-4">
          <div className="flex gap-2">
            {presetColors.map(c => (
              <button
                key={c}
                onClick={() => setAccentColor(c)}
                className={`w-8 h-8 rounded-full border-2 transition-all duration-200 ${
                  accentColor === c ? 'border-white scale-110' : 'border-transparent hover:scale-105'
                }`}
                style={{ backgroundColor: c, boxShadow: accentColor === c ? `0 0 12px ${c}` : 'none' }}
              />
            ))}
          </div>
          <input
            type="text"
            value={accentColor}
            onChange={e => setAccentColor(e.target.value)}
            className="w-24 bg-white/[0.04] border border-white/[0.1] rounded-lg px-2.5 py-2 text-[11px] font-mono text-white focus:outline-none focus:border-[#00FFFF]/50 transition-all"
          />
        </div>
      </FieldGroup>

      <FieldGroup label="Glow Intensity" hint={`${glowIntensity}% — Controls the neon glow effects`}>
        <input
          type="range"
          min={0}
          max={100}
          value={glowIntensity}
          onChange={e => setGlowIntensity(Number(e.target.value))}
          className="w-full h-1.5 bg-white/[0.08] rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#00FFFF] [&::-webkit-slider-thumb]:shadow-[0_0_8px_#00FFFF]"
        />
        <div className="mt-3 flex items-center gap-3">
          <div className="h-8 flex-1 rounded-lg border border-white/[0.06] flex items-center justify-center" style={{ boxShadow: `0 0 ${glowIntensity * 0.3}px ${accentColor}40` }}>
            <span className="text-[10px] font-mono" style={{ color: accentColor, textShadow: `0 0 ${glowIntensity * 0.2}px ${accentColor}` }}>PREVIEW</span>
          </div>
        </div>
      </FieldGroup>

      <FieldGroup label="Animation Speed" hint="Speed of transitions and micro-interactions">
        <div className="grid grid-cols-3 gap-3">
          {(['slow', 'normal', 'fast'] as const).map(s => (
            <button
              key={s}
              onClick={() => setAnimationSpeed(s)}
              className={`px-3 py-2.5 rounded-lg border text-[11px] font-medium capitalize transition-all duration-200 ${
                animationSpeed === s
                  ? 'border-[#00FFFF]/50 bg-[#00FFFF]/[0.06] text-[#00FFFF]'
                  : 'border-white/[0.08] bg-white/[0.02] text-white/50 hover:border-white/[0.15]'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </FieldGroup>
    </div>
  );
}

function AudioProcessingTab({
  autoSeparate,
  setAutoSeparate,
  model,
  setModel,
  stemCount,
  setStemCount: setStemCountProp,
}: {
  autoSeparate: boolean;
  setAutoSeparate: (v: boolean) => void;
  model: StemModelPref;
  setModel: (v: StemModelPref) => void;
  stemCount: number;
  setStemCount: (v: number) => void;
}) {
  const modelInfo = getModelInfo(model);

  return (
    <div className="space-y-8">
      <SectionHeader icon={Activity} title="Audio Processing" subtitle="Source separation and stem analysis powered by open-source transformers" />

      <div className="p-4 rounded-lg border border-[#FF6BFF]/10 bg-[#FF6BFF]/[0.02]">
        <div className="flex items-start gap-3">
          <Music size={14} className="text-[#FF6BFF] mt-0.5 shrink-0" />
          <div>
            <div className="text-[12px] font-medium text-white/80 mb-1">Demucs Integration</div>
            <div className="text-[11px] text-white/50 leading-relaxed">
              Connected to self-hosted Demucs endpoint for AI-powered music source separation.
              Multiple open-source transformer models available for varying quality/speed tradeoffs.
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <ToggleRow
          label="Auto-Separate on Ingest"
          description="Automatically queue stem separation when new tracks are imported"
          enabled={autoSeparate}
          onToggle={() => setAutoSeparate(!autoSeparate)}
        />
      </div>

      <FieldGroup label="Separation Model" hint="Choose the open-source transformer model for stem extraction">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {STEM_MODELS.map((m) => (
            <button
              key={m.id}
              onClick={() => setModel(m.id)}
              className={`px-4 py-3.5 rounded-lg border text-left transition-all duration-200 ${
                model === m.id
                  ? 'border-[#FF6BFF]/50 bg-[#FF6BFF]/[0.06] shadow-[0_0_12px_rgba(255,107,255,0.06)]'
                  : 'border-white/[0.08] bg-white/[0.02] hover:border-white/[0.15]'
              }`}
            >
              <div className={`text-[12px] font-medium mb-0.5 ${model === m.id ? 'text-[#FF6BFF]' : 'text-white/60'}`}>
                {m.label}
              </div>
              <div className="text-[9px] text-white/30 leading-relaxed">{m.description}</div>
              <div className="text-[9px] text-white/40 mt-1 font-mono">Max {m.maxStems} stems</div>
            </button>
          ))}
        </div>
      </FieldGroup>

      <FieldGroup label="Stem Count" hint={`Choose how many stems to extract (1\u2013${modelInfo.maxStems} for ${modelInfo.label})`}>
        <div className="flex items-center gap-4">
          <input
            type="range"
            min={1}
            max={modelInfo.maxStems}
            value={stemCount}
            onChange={(e) => setStemCountProp(Number(e.target.value))}
            className="flex-1 h-1.5 rounded-full appearance-none bg-white/10 accent-[#FF6BFF] cursor-pointer"
          />
          <span className="text-lg font-bold text-[#FF6BFF] tabular-nums w-8 text-center">{stemCount}</span>
        </div>
      </FieldGroup>

      <FieldGroup label="Stem Output Preview" hint="Stems extracted per track with the selected model and count">
        <div className="space-y-1.5">
          {modelInfo.stemLabels.slice(0, stemCount).map((stem, i) => {
            const colors = ['#FF6BFF', '#FF003C', '#00FFFF', '#39FF14', '#FFD600', '#8B8B8B'];
            return (
              <div key={stem} className="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-white/[0.06] bg-white/[0.02]">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: colors[i % colors.length] }} />
                <span className="text-[12px] font-mono text-white/70 flex-1">{stem}</span>
                <span className="text-[9px] font-mono text-white/30 uppercase">Isolated</span>
              </div>
            );
          })}
        </div>
      </FieldGroup>

      <FieldGroup label="Review Workflow" hint="How separated stems are processed after extraction">
        <div className="space-y-2">
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-white/[0.06] bg-white/[0.02]">
            <span className="text-[10px] font-mono text-[#C4AB04] bg-[#C4AB04]/10 px-2 py-0.5 rounded">1</span>
            <span className="text-[11px] text-white/60">Agent separates stems and generates confidence scores</span>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-white/[0.06] bg-white/[0.02]">
            <span className="text-[10px] font-mono text-[#C4AB04] bg-[#C4AB04]/10 px-2 py-0.5 rounded">2</span>
            <span className="text-[11px] text-white/60">Work item appears in Agent Queue for human review</span>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-white/[0.06] bg-white/[0.02]">
            <span className="text-[10px] font-mono text-[#C4AB04] bg-[#C4AB04]/10 px-2 py-0.5 rounded">3</span>
            <span className="text-[11px] text-white/60">Human verifies each stem, overrides labels and notes as needed</span>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-white/[0.06] bg-white/[0.02]">
            <span className="text-[10px] font-mono text-[#39FF14] bg-[#39FF14]/10 px-2 py-0.5 rounded">4</span>
            <span className="text-[11px] text-white/60">Approved stems become downloadable assets on track detail</span>
          </div>
        </div>
      </FieldGroup>

      <div className="p-4 rounded-lg border border-white/[0.06] bg-white/[0.01]">
        <div className="flex items-center gap-3 mb-2">
          <Sliders size={13} className="text-white/40" />
          <span className="text-[11px] font-medium text-white/60">Endpoint Status</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[10px] font-mono">
          <div><span className="text-white/30">Backend</span><br /><span className="text-[#FF6BFF]">Self-hosted</span></div>
          <div><span className="text-white/30">Model</span><br /><span className="text-white/70">{model}</span></div>
          <div><span className="text-white/30">Stems</span><br /><span className="text-white/70">{stemCount}</span></div>
          <div><span className="text-white/30">Avg Time</span><br /><span className="text-white/70">~30s/track</span></div>
        </div>
      </div>
    </div>
  );
}

function CapabilitiesTab() {
  const capabilities = [
    { id: 'nl', label: 'Natural Language', code: 'NL', description: 'Parse and execute natural language commands', active: true, icon: BookOpen },
    { id: 'fn', label: 'Function Calls', code: 'FN', description: 'Direct function invocation via structured calls', active: true, icon: Terminal },
    { id: 'pipe', label: 'Pipelines', code: 'PIPE', description: 'Chain multiple operations sequentially', active: true, icon: Layers },
    { id: 'plan', label: 'Plan Decomposition', code: 'PLAN', description: 'Break complex goals into execution steps', active: true, icon: Network },
    { id: 'mem', label: 'Working Memory', code: 'MEM', description: 'Persistent context across operations', active: true, icon: Brain },
    { id: 'cot', label: 'Chain-of-Thought', code: 'COT', description: 'Explicit reasoning before execution', active: true, icon: Sparkles },
    { id: 'swarm', label: 'Swarm Protocol', code: 'SWARM', description: 'Multi-node distributed processing', active: true, icon: Radio },
    { id: 'eval', label: 'Evaluation', code: 'EVAL', description: 'Self-assessment and quality scoring', active: true, icon: Eye },
    { id: 'dag', label: 'DAG Execution', code: 'DAG', description: 'Directed acyclic graph task scheduling', active: false, icon: Network },
  ];

  return (
    <div className="space-y-8">
      <SectionHeader icon={Shield} title="Protocol Capabilities" subtitle="Active capabilities on the agent runtime" />

      <div className="space-y-2">
        {capabilities.map(cap => (
          <div
            key={cap.id}
            className={`flex items-center gap-4 px-4 py-3.5 rounded-lg border transition-all duration-200 ${
              cap.active
                ? 'border-white/[0.08] bg-white/[0.02]'
                : 'border-white/[0.04] bg-white/[0.01] opacity-50'
            }`}
          >
            <cap.icon size={16} className={cap.active ? 'text-[#00FFFF]' : 'text-white/20'} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-[12px] font-medium ${cap.active ? 'text-white' : 'text-white/40'}`}>{cap.label}</span>
                <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/[0.04] text-white/40">{cap.code}</span>
              </div>
              <div className="text-[10px] text-white/30 mt-0.5">{cap.description}</div>
            </div>
            <span className={`w-2 h-2 rounded-full ${cap.active ? 'bg-[#39FF14] shadow-[0_0_6px_#39FF14]' : 'bg-white/10'}`} />
          </div>
        ))}
      </div>

      <div className="p-4 rounded-lg border border-white/[0.06] bg-white/[0.01]">
        <div className="flex items-center gap-3 mb-2">
          <Sliders size={13} className="text-white/40" />
          <span className="text-[11px] font-medium text-white/60">Runtime Info</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[10px] font-mono">
          <div><span className="text-white/30">Protocol</span><br /><span className="text-[#00FFFF]">mediablox/1.0</span></div>
          <div><span className="text-white/30">Runtime</span><br /><span className="text-white/70">v2.4.1</span></div>
          <div><span className="text-white/30">Uptime</span><br /><span className="text-[#39FF14]">99.8%</span></div>
          <div><span className="text-white/30">Latency</span><br /><span className="text-white/70">12ms avg</span></div>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, subtitle }: { icon: typeof Bot; title: string; subtitle: string }) {
  return (
    <div className="flex items-center gap-3 mb-2">
      <div className="w-9 h-9 rounded-lg bg-[#00FFFF]/[0.06] border border-[#00FFFF]/20 flex items-center justify-center">
        <Icon size={16} className="text-[#00FFFF]" />
      </div>
      <div>
        <h2 className="text-[14px] font-semibold text-white">{title}</h2>
        <p className="text-[11px] text-white/40">{subtitle}</p>
      </div>
    </div>
  );
}

function FieldGroup({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[12px] font-medium text-white/70 mb-1.5">{label}</label>
      {hint && <p className="text-[10px] text-white/30 mb-3">{hint}</p>}
      {children}
    </div>
  );
}

function ToggleRow({ label, description, enabled, onToggle }: { label: string; description: string; enabled: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center gap-4 px-4 py-3 rounded-lg border border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12] transition-all duration-200"
    >
      <div className="flex-1 text-left">
        <div className="text-[12px] font-medium text-white/80">{label}</div>
        <div className="text-[10px] text-white/30">{description}</div>
      </div>
      <div
        className={`relative inline-flex h-5 w-10 items-center rounded-full shrink-0 transition-colors duration-300 ${
          enabled ? 'bg-[#39FF14]' : 'bg-white/15'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full shadow-md transition-transform duration-300 ${
            enabled ? 'translate-x-5 bg-black' : 'translate-x-0.5 bg-white'
          }`}
        />
      </div>
    </button>
  );
}
